import { ProfileNotFound } from '@/components/not-found/profile-not-found';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  ScrollView,
  Pressable,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';

import { ProfessionalPills } from '@/components/public-profile/professional-pills';
import { getFirstName, joinFirstNames } from '@/components/team/member-avatar';
import { ThemedText } from '@/components/themed-text';
import { Card } from '@/components/ui/card';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SearchInput } from '@/components/ui/search-input';
import { Pagination } from '@/components/ui/pagination';
import { EmptyState } from '@/components/ui/empty-state';
import { buildWhatsappLink, usePublicProfileView } from '@/hooks/use-public-profile';
import { PublicBrandHeader } from '@/components/public/public-brand-header';
import { PublicBrandFooter } from '@/components/public/public-brand-footer';
import { COVER_ASPECT } from '@/constants/cover';
import { PublicProfileView } from '@/types/public-profile';
import { useDistanceTo } from '@/hooks/use-distance-to';
import { resolveTerms } from '@/hooks/use-terms';
import { useThemedHex, useIconChip } from '@/hooks/use-theme-colors';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';
import { SeoHead } from '@/components/seo-head';
import {
  paymentMethodLabel as sharedPaymentMethodLabel,
  priceText,
  profileAnswer,
  profileBranch,
  profileFaq,
  profileMeta,
} from '@/lib/seo/profile-meta';

export const DEFAULT_BRAND_COLOR = '#FD5001';
const SERVICES_PER_PAGE = 6;
const GALLERY_PER_PAGE = 8;

/** O mesmo rótulo do HTML que o buscador lê (`lib/seo/profile-meta`). */
const paymentMethodLabel = sharedPaymentMethodLabel;

export function formatPhoneBR(raw: string): string {
  const d = raw.replace(/\D/g, '').replace(/^55/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return raw;
}

function mapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

type Service = NonNullable<PublicProfileView['services']>[number];
type Photo = NonNullable<PublicProfileView['gallery']>[number];

/**
 * Um card da lista de serviços. Na equipe, `copies` são os profissionais que
 * fazem o serviço (a mesma `catalogItemId`), e `service` é a cópia do primeiro.
 */
type ServiceEntry = {
  key: string;
  service: Service;
  copies: { providerId: string; name: string }[];
};

/** "com Ana", "com Ana e Pedro", "com Ana, Pedro +1". */
function attendedByLabel(names: string[]): string {
  if (names.length <= 2) return `com ${joinFirstNames(names)}`;
  return `com ${names.slice(0, 2).map(getFirstName).join(', ')} +${names.length - 2}`;
}

/** Grid responsivo: colunas iguais com espaçamento, sem overflow horizontal. */
export function Grid({
  columns,
  gutter = 12,
  children,
}: {
  columns: number;
  gutter?: number;
  children: React.ReactNode;
}) {
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -gutter / 2 }}>
      {React.Children.map(children, (child) => (
        <View
          style={{
            width: `${100 / columns}%`,
            paddingHorizontal: gutter / 2,
            marginBottom: gutter,
          }}
        >
          {child}
        </View>
      ))}
    </View>
  );
}

export function SectionHeader({
  icon,
  title,
  count,
  brandColor,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  count?: number;
  brandColor: string;
}) {
  const chip = useIconChip();
  return (
    <View className="flex-row items-center gap-3">
      <View
        className="h-10 w-10 items-center justify-center rounded-2xl"
        style={{ backgroundColor: chip(brandColor, '1A').bg, borderCurve: 'continuous' }}
      >
        <Ionicons name={icon} size={20} color={chip(brandColor, '1A').fg} />
      </View>
      <ThemedText role="heading" aria-level={2} className="flex-1 font-bold text-xl">
        {title}
      </ThemedText>
      {count != null && count > 0 && (
        <View className="rounded-full px-2.5 py-1" style={{ backgroundColor: `${brandColor}14` }}>
          <ThemedText className="font-bold text-xs" style={{ color: brandColor }}>
            {count}
          </ThemedText>
        </View>
      )}
    </View>
  );
}

function InfoRow({
  icon,
  label,
  brandColor,
  first,
  onPress,
  children,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  brandColor: string;
  first?: boolean;
  onPress?: () => void;
  children: React.ReactNode;
}) {
  const chip = useIconChip();
  const Wrapper: any = onPress ? Pressable : View;
  return (
    <Wrapper
      onPress={onPress}
      className={`flex-row items-center gap-3 py-3.5 ${first ? '' : 'border-t border-neutral-100'}`}
    >
      <View
        className="h-9 w-9 items-center justify-center rounded-xl"
        style={{ backgroundColor: chip(brandColor, '14').bg, borderCurve: 'continuous' }}
      >
        <Ionicons name={icon} size={18} color={chip(brandColor, '14').fg} />
      </View>
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-[11px] uppercase tracking-wide text-neutral-400">
          {label}
        </ThemedText>
        {children}
      </View>
      {onPress && <Ionicons name="chevron-forward" size={16} color="#C4C4C4" />}
    </Wrapper>
  );
}

function ProfileInfoCard({
  info,
  brandColor,
  phone,
  onPhone,
  onMap,
  onInstagram,
  onWebsite,
}: {
  info: NonNullable<PublicProfileView['info']>;
  brandColor: string;
  phone: string | null;
  onPhone: () => void;
  onMap: () => void;
  onInstagram: () => void;
  onWebsite: () => void;
}) {
  const rows: React.ReactNode[] = [];

  if (info.businessHours) {
    rows.push(
      <InfoRow key="hours" icon="time-outline" label="Horário" brandColor={brandColor}>
        <ThemedText className="text-sm text-neutral-800">{info.businessHours}</ThemedText>
      </InfoRow>,
    );
  }
  if (phone) {
    rows.push(
      <InfoRow
        key="phone"
        icon="call-outline"
        label="Telefone"
        brandColor={brandColor}
        onPress={onPhone}
      >
        <ThemedText className="font-medium text-sm" style={{ color: brandColor }}>
          {formatPhoneBR(phone)}
        </ThemedText>
      </InfoRow>,
    );
  }
  if (info.address) {
    rows.push(
      <InfoRow key="address" icon="location-outline" label="Endereço" brandColor={brandColor}>
        <ThemedText className="text-sm text-neutral-800">{info.address}</ThemedText>
        <Pressable className="mt-1 flex-row items-center gap-1" onPress={onMap}>
          <Ionicons name="map-outline" size={13} color={brandColor} />
          <ThemedText className="font-semibold text-xs" style={{ color: brandColor }}>
            Ver no mapa
          </ThemedText>
        </Pressable>
      </InfoRow>,
    );
  }
  if (info.paymentMethods.length > 0) {
    rows.push(
      <InfoRow
        key="payment"
        icon="card-outline"
        label="Formas de pagamento"
        brandColor={brandColor}
      >
        <View className="mt-1 flex-row flex-wrap gap-2">
          {info.paymentMethods.map((m) => (
            <View
              key={m}
              className="rounded-lg px-2.5 py-1"
              style={{ backgroundColor: `${brandColor}14`, borderCurve: 'continuous' }}
            >
              <ThemedText className="font-semibold text-xs" style={{ color: brandColor }}>
                {paymentMethodLabel(m)}
              </ThemedText>
            </View>
          ))}
        </View>
      </InfoRow>,
    );
  }
  if (info.socialLinks?.instagram) {
    rows.push(
      <InfoRow
        key="instagram"
        icon="logo-instagram"
        label="Instagram"
        brandColor={brandColor}
        onPress={onInstagram}
      >
        <ThemedText className="font-medium text-sm" style={{ color: brandColor }}>
          {info.socialLinks.instagram}
        </ThemedText>
      </InfoRow>,
    );
  }
  if (info.socialLinks?.website) {
    rows.push(
      <InfoRow
        key="website"
        icon="globe-outline"
        label="Site"
        brandColor={brandColor}
        onPress={onWebsite}
      >
        <ThemedText className="font-medium text-sm" style={{ color: brandColor }}>
          {info.socialLinks.website}
        </ThemedText>
      </InfoRow>,
    );
  }

  if (rows.length === 0) return null;

  return (
    <Card>
      <View className="px-5 py-1.5">
        {rows.map((row, i) =>
          React.isValidElement(row) ? React.cloneElement(row, { first: i === 0 } as any) : row,
        )}
      </View>
    </Card>
  );
}

export function ServiceCard({
  service,
  brandColor,
  attendedBy,
  onPress,
}: {
  service: Service;
  brandColor: string;
  /** Quem da equipe faz o serviço, na visão "Todos". */
  attendedBy?: string;
  onPress?: () => void;
}) {
  const th = useThemedHex();
  // Mesma regra do HTML para buscador: oculto, vazio ou zero é "sob
  // consulta"; assinatura mostra o ciclo ("R$ 120,00 por mês").
  const visible = priceText(service);
  const priceLabel = visible ?? 'Valor sob consulta';
  const isConsult = !visible;
  // Agendável no portal quando tem duração e o visitante é um cliente logado.
  const bookable = !!onPress && service.durationMinutes != null;

  const body = (
    <View className="gap-2 p-4">
      <ThemedText className="font-semibold text-base" numberOfLines={2}>
        {service.name}
      </ThemedText>
      {!!attendedBy && (
        <View className="flex-row items-center gap-1.5">
          <Ionicons className="text-neutral-500" name="people-outline" size={13} />
          <ThemedText className="font-semibold text-xs text-neutral-500">{attendedBy}</ThemedText>
        </View>
      )}
      {!!service.description && (
        <ThemedText numberOfLines={3} className="text-sm leading-5 text-neutral-500">
          {service.description}
        </ThemedText>
      )}
      <View className="mt-1 flex-row items-center justify-between gap-2">
        <View
          className="self-start rounded-full px-3 py-1"
          style={{ backgroundColor: isConsult ? th('#F5F5F5') : `${brandColor}14` }}
        >
          <ThemedText
            className="font-bold text-sm"
            style={{ color: isConsult ? th('#737373') : brandColor }}
          >
            {priceLabel}
          </ThemedText>
        </View>
        {bookable && (
          <View
            className="flex-row items-center gap-1 rounded-full px-3 py-1.5"
            style={{ backgroundColor: brandColor }}
          >
            <Ionicons name="calendar-outline" size={13} color="#fff" />
            <ThemedText className="font-bold text-xs text-white">Agendar</ThemedText>
          </View>
        )}
      </View>
    </View>
  );

  // flex-1, não h-full: estica até o card mais alto da linha do Grid. h-full no
  // nativo é 100% de um pai sem altura e estoura a altura do card.
  if (bookable) {
    return (
      <PressableScale haptic onPress={onPress} className="flex-1">
        <Card className="flex-1">{body}</Card>
      </PressableScale>
    );
  }
  return <Card className="flex-1">{body}</Card>;
}

export function GalleryCard({ photo }: { photo: Photo }) {
  return (
    <View
      className="overflow-hidden rounded-2xl bg-neutral-100"
      style={{ borderCurve: 'continuous' }}
    >
      {photo.beforeUrl ? (
        <View className="flex-row">
          <View style={{ flex: 1 }}>
            <Image
              source={{ uri: photo.beforeUrl }}
              accessibilityLabel="Antes"
              style={{ width: '100%', aspectRatio: 1 }}
              resizeMode="cover"
            />
            <View className="absolute left-1.5 top-1.5 rounded-full bg-black/55 px-2 py-0.5">
              <ThemedText className="font-semibold text-[10px] text-white">Antes</ThemedText>
            </View>
          </View>
          <View style={{ flex: 1 }}>
            <Image
              source={{ uri: photo.url }}
              accessibilityLabel="Depois"
              style={{ width: '100%', aspectRatio: 1 }}
              resizeMode="cover"
            />
            <View className="absolute right-1.5 top-1.5 rounded-full bg-black/55 px-2 py-0.5">
              <ThemedText className="font-semibold text-[10px] text-white">Depois</ThemedText>
            </View>
          </View>
        </View>
      ) : (
        <Image
          source={{ uri: photo.url }}
          accessibilityLabel="Foto do trabalho"
          style={{ width: '100%', aspectRatio: 1 }}
          resizeMode="cover"
        />
      )}
      {!!photo.caption && (
        <View className="absolute bottom-0 left-0 right-0">
          <LinearGradient colors={['transparent', 'rgba(0,0,0,0.7)']}>
            <ThemedText
              numberOfLines={2}
              className="px-2.5 pb-2 pt-6 font-medium text-xs text-white"
            >
              {photo.caption}
            </ThemedText>
          </LinearGradient>
        </View>
      )}
    </View>
  );
}

export function PublicProfilePage({
  slug,
  initialProfessionalId,
  onServicePress,
}: {
  slug: string;
  /** Abre a página com esse profissional da equipe já escolhido. */
  initialProfessionalId?: string | null;
  /**
   * Habilita agendamento inline: chamado ao tocar num serviço agendável. O
   * `professionalId` vem quando o serviço é de outro profissional da equipe.
   */
  onServicePress?: (serviceId: string, professionalId?: string) => void;
}) {
  const th = useThemedHex();
  const { data: profile, isLoading, error } = usePublicProfileView(slug);
  const { width } = useWindowDimensions();

  const scrollRef = useRef<ScrollView>(null);
  const infoRef = useRef<View>(null);
  const distance = useDistanceTo(profile?.latitude, profile?.longitude, profile?.info?.address);

  const scrollToInfo = () => {
    const node = scrollRef.current?.getInnerViewNode?.();
    if (!node || !infoRef.current) return;
    infoRef.current.measureLayout(
      node,
      (_x, y) => scrollRef.current?.scrollTo({ y: Math.max(0, y - 12), animated: true }),
      () => {},
    );
  };

  const [serviceQuery, setServiceQuery] = useState('');
  const [galleryQuery, setGalleryQuery] = useState('');
  const [servicePage, setServicePage] = useState(1);
  const [galleryPage, setGalleryPage] = useState(1);
  // Profissional escolhido na seção Equipe; null = todos.
  const [pickedProfessionalId, setPickedProfessionalId] = useState<string | null>(
    initialProfessionalId ?? null,
  );
  useEffect(() => setPickedProfessionalId(initialProfessionalId ?? null), [initialProfessionalId]);

  useEffect(() => setServicePage(1), [serviceQuery, pickedProfessionalId]);
  useEffect(() => setGalleryPage(1), [galleryQuery]);

  // O profissional mora dentro da página da organização: o slug dele abre a
  // página dele lá. Sem página publicada do dono não há para onde mandar, e a
  // página dele se vira sozinha com a marca da organização.
  const organizationTarget = profile?.organization ?? null;
  useEffect(() => {
    if (!organizationTarget) return;
    router.replace({
      pathname: '/[handle]/professional/[providerId]',
      params: {
        handle: `@${organizationTarget.slug}`,
        providerId: organizationTarget.providerId,
      },
    } as never);
  }, [organizationTarget]);

  const isTablet = width >= 768;
  const isWide = width >= 1024;
  const containerMax = isWide ? 1120 : isTablet ? 760 : width;
  const avatarSize = isWide ? 128 : isTablet ? 112 : 80;
  // Quanto da foto sobe na capa; o nome começa logo abaixo da borda dela.
  const avatarOverlap = Math.round(avatarSize * 0.45);
  const serviceColumns = isWide ? 3 : isTablet ? 2 : 1;
  const galleryColumns = isWide ? 4 : isTablet ? 3 : 2;

  // Equipe só com 2+ que atendem; fora disso a página é a de sempre.
  const team = (profile?.team?.length ?? 0) >= 2 ? (profile?.team ?? null) : null;
  // Id vindo do link só vale se a pessoa estiver mesmo na equipe.
  const professionalId = team?.some((m) => m.providerId === pickedProfessionalId)
    ? pickedProfessionalId
    : null;
  const terms = useMemo(() => resolveTerms(profile?.vocabulary), [profile?.vocabulary]);

  // "Todos" junta as cópias do mesmo serviço (`catalogItemId`) num card só; com
  // alguém escolhido, só os serviços dele.
  const serviceEntries = useMemo<ServiceEntry[]>(() => {
    if (!team) {
      return (profile?.services ?? []).map((service) => ({
        key: service.id,
        service,
        copies: [],
      }));
    }

    // Com catálogo, o card é do negócio: nome, descrição e preço saem do item,
    // e a cópia só entra para dizer quem faz e por quanto.
    const catalog = profile?.catalog ?? null;
    if (catalog?.length) {
      const entries: ServiceEntry[] = [];
      for (const item of catalog) {
        const doing = professionalId
          ? item.professionals.filter((p) => p.providerId === professionalId)
          : item.professionals;
        if (!doing.length) continue;
        const prices = doing.map((p) => p.price).filter((price): price is number => price != null);
        const sameValue = new Set(prices).size <= 1;
        entries.push({
          key: item.id,
          service: {
            // A cópia de quem faz é o que o agendamento abre.
            id: doing[0].serviceId,
            name: item.name,
            description: item.description,
            price: prices.length ? Math.min(...prices) : null,
            priceDisplayMode: sameValue ? doing[0].priceDisplayMode : 'from',
            durationMinutes: doing[0].durationMinutes ?? item.durationMinutes,
            billingInterval: doing[0].billingInterval ?? null,
            catalogItemId: item.id,
          },
          copies: doing.map((p) => ({ providerId: p.providerId, name: p.name })),
        });
      }
      return entries;
    }

    const members = professionalId ? team.filter((m) => m.providerId === professionalId) : team;
    const groups = new Map<string, ServiceEntry>();
    for (const member of members) {
      for (const service of member.services) {
        const key = service.catalogItemId ?? service.id;
        const copy = { providerId: member.providerId, name: member.name };
        const group = groups.get(key);
        if (group) group.copies.push(copy);
        else groups.set(key, { key, service, copies: [copy] });
      }
    }
    return [...groups.values()];
  }, [profile?.services, profile?.catalog, team, professionalId]);

  const filteredServices = useMemo(() => {
    const q = serviceQuery.trim().toLowerCase();
    if (!q) return serviceEntries;
    return serviceEntries.filter(
      ({ service: s }) =>
        s.name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q),
    );
  }, [serviceEntries, serviceQuery]);

  // A galeria da equipe é a união dos trabalhos; escolher alguém mostra só os
  // dele (foto antiga, sem `providerId`, continua na página de todos).
  const providerGallery = useMemo(() => {
    const all = profile?.gallery ?? [];
    return professionalId ? all.filter((p) => p.providerId === professionalId) : all;
  }, [profile?.gallery, professionalId]);

  const filteredGallery = useMemo(() => {
    const list = providerGallery;
    const q = galleryQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => (p.caption ?? '').toLowerCase().includes(q));
  }, [providerGallery, galleryQuery]);

  // Com destino de organização a tela está de saída: só o indicador, para o
  // perfil do profissional não piscar antes da troca.
  if (isLoading || organizationTarget) {
    return (
      <View className="flex-1 items-center justify-center bg-neutral-50">
        <ActivityIndicator size="large" color={DEFAULT_BRAND_COLOR} />
      </View>
    );
  }

  if (error || !profile) {
    return <ProfileNotFound slug={slug} />;
  }

  const brandColor = profile.brandColor ?? DEFAULT_BRAND_COLOR;
  const displayName = profile.businessName;
  // Título, resposta e perguntas saem das mesmas funções que escrevem o HTML
  // que o buscador lê (`api/profile-page.ts`): a tela e o HTML dizem o mesmo.
  const meta = profileMeta(profile);
  const answer = profileAnswer(profile);
  const faq = profileFaq(profile);
  const branch = profileBranch(profile);
  const avatarUri = profile.avatarUrl ?? profile.logoUrl;
  const initial = displayName.slice(0, 1).toUpperCase();

  const serviceTotalPages = Math.ceil(filteredServices.length / SERVICES_PER_PAGE) || 1;
  const galleryTotalPages = Math.ceil(filteredGallery.length / GALLERY_PER_PAGE) || 1;
  const pagedServices = filteredServices.slice(
    (servicePage - 1) * SERVICES_PER_PAGE,
    servicePage * SERVICES_PER_PAGE,
  );
  const pagedGallery = filteredGallery.slice(
    (galleryPage - 1) * GALLERY_PER_PAGE,
    galleryPage * GALLERY_PER_PAGE,
  );

  const hasServices = team
    ? team.some((m) => m.services.length > 0)
    : (profile.services?.length ?? 0) > 0;
  const hasGallery = providerGallery.length > 0;

  // Com alguém escolhido, o serviço já é a cópia dessa pessoa. Em "Todos" vai
  // sem profissional: a tela do serviço abre em "qualquer profissional" e quem
  // atende sai junto com o horário.
  const pressService = (entry: ServiceEntry) => {
    onServicePress?.(entry.service.id, professionalId ?? undefined);
  };

  const openWhatsapp = () => {
    if (!profile.whatsappNumber) return;
    void Linking.openURL(buildWhatsappLink(profile.whatsappNumber, 'profile'));
  };
  const openInstagram = () => {
    const ig = profile.info?.socialLinks?.instagram;
    if (!ig) return;
    void Linking.openURL(`https://instagram.com/${ig.replace(/^@/, '')}`);
  };
  const openWebsite = () => {
    const site = profile.info?.socialLinks?.website;
    if (site) void Linking.openURL(site);
  };
  const openMap = () => {
    const addr = profile.info?.address;
    if (addr) void Linking.openURL(mapsUrl(addr));
  };

  const Avatar = (
    <View
      className="items-center justify-center overflow-hidden bg-card"
      style={{
        width: avatarSize,
        height: avatarSize,
        borderRadius: avatarSize / 2,
        borderWidth: 4,
        borderColor: th('#fff'),
        boxShadow: '0 6px 20px rgba(23,17,10,0.18)',
      }}
    >
      {avatarUri ? (
        <Image
          source={{ uri: avatarUri }}
          accessibilityLabel={`Logo de ${displayName}`}
          style={{ width: '100%', height: '100%' }}
        />
      ) : (
        <View
          className="h-full w-full items-center justify-center"
          style={{ backgroundColor: brandColor }}
        >
          <ThemedText className="font-bold text-white" style={{ fontSize: avatarSize / 2.4 }}>
            {initial}
          </ThemedText>
        </View>
      )}
    </View>
  );

  return (
    <View className="flex-1 bg-neutral-50">
      <SeoHead title={meta.title} description={meta.description} path={meta.path} />
      <KeyboardAwareScrollView
        keyboardShouldPersistTaps="handled"
        bottomOffset={KEYBOARD_BOTTOM_OFFSET}
        ref={scrollRef}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 48 }}
      >
        <PublicBrandHeader />
        <View className="mx-auto w-full" style={{ maxWidth: containerMax }}>
          {/* Capa */}
          <View
            className="overflow-hidden"
            style={{
              borderBottomLeftRadius: isTablet ? 28 : 0,
              borderBottomRightRadius: isTablet ? 28 : 0,
              borderCurve: 'continuous',
            }}
          >
            {profile.coverUrl ? (
              <Image
                source={{ uri: profile.coverUrl }}
                accessibilityLabel={`Capa de ${displayName}`}
                style={{ width: '100%', aspectRatio: COVER_ASPECT }}
                resizeMode="cover"
              />
            ) : (
              <LinearGradient
                colors={[brandColor, `${brandColor}B3`]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{ width: '100%', aspectRatio: COVER_ASPECT }}
              />
            )}
          </View>

          {/* Identidade: foto à esquerda sobe na capa, nome ao lado — nada a centralizar */}
          <View className={`flex-row items-start gap-3 ${isTablet ? 'gap-5 px-6' : 'px-4'}`}>
            <View style={{ marginTop: -avatarOverlap }}>{Avatar}</View>
            <View className={`min-w-0 flex-1 items-start ${isTablet ? 'pt-4' : 'pt-2.5'}`}>
              <ThemedText
                role="heading"
                aria-level={1}
                numberOfLines={2}
                className={isTablet ? 'font-bold text-3xl' : 'font-bold text-xl leading-7'}
              >
                {displayName}
              </ThemedText>
              {!!branch && (
                <View
                  className="mt-1.5 rounded-full px-3 py-1"
                  style={{ backgroundColor: `${brandColor}1A` }}
                >
                  <ThemedText
                    numberOfLines={1}
                    className="font-semibold text-xs"
                    style={{ color: brandColor }}
                  >
                    {branch}
                  </ThemedText>
                </View>
              )}
            </View>
          </View>

          <View className={isTablet ? 'mt-4 flex-row items-start gap-6 px-6' : 'mt-3 gap-3 px-4'}>
            <View className={isTablet ? 'min-w-0 flex-1 gap-2.5' : 'gap-2.5'}>
              {(!!distance || !!profile.info) && (
                <View className="flex-row flex-wrap items-center gap-x-4 gap-y-1.5">
                  {!!distance && (
                    <View className="flex-row items-center gap-1">
                      <Ionicons className="text-neutral-500" name="location-outline" size={14} />
                      <ThemedText className="text-[13px] text-neutral-600">{distance}</ThemedText>
                    </View>
                  )}
                  {!!profile.info && (
                    <PressableScale
                      haptic
                      onPress={scrollToInfo}
                      className="flex-row items-center gap-1"
                    >
                      <Ionicons name="information-circle-outline" size={14} color={brandColor} />
                      <ThemedText
                        className="font-semibold text-[13px]"
                        style={{ color: brandColor }}
                      >
                        Ver informações
                      </ThemedText>
                    </PressableScale>
                  )}
                </View>
              )}

              {!!answer && (
                <ThemedText className="text-[15px] leading-6 text-neutral-700">{answer}</ThemedText>
              )}

              {!!profile.bio && (
                <ThemedText className="text-[15px] leading-6 text-neutral-600">
                  {profile.bio}
                </ThemedText>
              )}
            </View>

            {/* CTAs: o WhatsApp estica no celular, os ícones têm tamanho fixo */}
            {(!!profile.whatsappNumber ||
              !!profile.info?.socialLinks?.instagram ||
              !!profile.info?.socialLinks?.website) && (
              <View className={isTablet ? 'shrink-0 flex-row gap-2' : 'flex-row gap-2'}>
                {!!profile.whatsappNumber && (
                  <PressableScale
                    haptic
                    onPress={openWhatsapp}
                    className={`h-12 flex-row items-center justify-center gap-2 rounded-full px-5 ${isTablet ? '' : 'min-w-0 flex-1'}`}
                    style={{ backgroundColor: brandColor, borderCurve: 'continuous' }}
                  >
                    <Ionicons name="logo-whatsapp" size={18} color="#fff" />
                    <ThemedText numberOfLines={1} className="font-bold text-sm text-white">
                      {isTablet ? 'Chamar no WhatsApp' : 'WhatsApp'}
                    </ThemedText>
                  </PressableScale>
                )}
                {!!profile.info?.socialLinks?.instagram && (
                  <PressableScale
                    onPress={openInstagram}
                    className="h-12 w-12 items-center justify-center rounded-full bg-card"
                    style={{
                      boxShadow: '0 1px 2px rgba(23,17,10,0.04), 0 6px 16px rgba(23,17,10,0.06)',
                    }}
                  >
                    <Ionicons name="logo-instagram" size={22} color={brandColor} />
                  </PressableScale>
                )}
                {!!profile.info?.socialLinks?.website && (
                  <PressableScale
                    onPress={openWebsite}
                    className="h-12 w-12 items-center justify-center rounded-full bg-card"
                    style={{
                      boxShadow: '0 1px 2px rgba(23,17,10,0.04), 0 6px 16px rgba(23,17,10,0.06)',
                    }}
                  >
                    <Ionicons name="globe-outline" size={22} color={brandColor} />
                  </PressableScale>
                )}
              </View>
            )}
          </View>

          <View className={`gap-8 ${isTablet ? 'px-6' : 'px-4'} pt-8`}>
            {/* Equipe: escolher alguém filtra os serviços logo abaixo */}
            {team && hasServices && (
              <View className="gap-4">
                <SectionHeader
                  icon="people-outline"
                  title={terms.title.professionalPlural}
                  count={team.length}
                  brandColor={brandColor}
                />
                <ProfessionalPills
                  options={team}
                  selectedId={professionalId}
                  onSelect={setPickedProfessionalId}
                  brandColor={brandColor}
                  allLabel={terms.agree('Todos', 'professionalPlural')}
                />
              </View>
            )}

            {/* Serviços */}
            {hasServices && (
              <View className="gap-4">
                <SectionHeader
                  icon="pricetags-outline"
                  title="Serviços"
                  count={serviceEntries.length}
                  brandColor={brandColor}
                />
                {serviceEntries.length > 4 && (
                  <SearchInput
                    value={serviceQuery}
                    onChangeText={setServiceQuery}
                    placeholder="Buscar serviço..."
                  />
                )}
                {pagedServices.length > 0 ? (
                  <Grid columns={serviceColumns}>
                    {pagedServices.map((entry) => (
                      <ServiceCard
                        key={entry.key}
                        service={entry.service}
                        brandColor={brandColor}
                        attendedBy={
                          team && !professionalId
                            ? attendedByLabel(entry.copies.map((c) => c.name))
                            : undefined
                        }
                        onPress={onServicePress ? () => pressService(entry) : undefined}
                      />
                    ))}
                  </Grid>
                ) : (
                  <EmptyState
                    icon="search-outline"
                    title="Nenhum serviço encontrado"
                    description={
                      serviceQuery.trim()
                        ? 'Tente buscar por outro termo.'
                        : 'Veja os de outra pessoa da equipe.'
                    }
                  />
                )}
                {serviceTotalPages > 1 && (
                  <Pagination
                    currentPage={servicePage}
                    totalPages={serviceTotalPages}
                    onPageChange={setServicePage}
                    brandColor={brandColor}
                    className="justify-center"
                  />
                )}
              </View>
            )}

            {/* Trabalhos */}
            {hasGallery && (
              <View className="gap-4">
                <SectionHeader
                  icon="images-outline"
                  title="Trabalhos"
                  count={providerGallery.length}
                  brandColor={brandColor}
                />
                {providerGallery.some((p) => !!p.caption) && providerGallery.length > 4 && (
                  <SearchInput
                    value={galleryQuery}
                    onChangeText={setGalleryQuery}
                    placeholder="Buscar trabalho..."
                  />
                )}
                {pagedGallery.length > 0 ? (
                  <Grid columns={galleryColumns} gutter={10}>
                    {pagedGallery.map((photo) => (
                      <GalleryCard key={photo.id} photo={photo} />
                    ))}
                  </Grid>
                ) : (
                  <EmptyState
                    icon="search-outline"
                    title="Nenhum trabalho encontrado"
                    description="Tente buscar por outro termo."
                  />
                )}
                {galleryTotalPages > 1 && (
                  <Pagination
                    currentPage={galleryPage}
                    totalPages={galleryTotalPages}
                    onPageChange={setGalleryPage}
                    brandColor={brandColor}
                    className="justify-center"
                  />
                )}
              </View>
            )}

            {/* Informações */}
            {profile.info && (
              <View ref={infoRef} className="gap-4">
                <SectionHeader
                  icon="information-circle-outline"
                  title="Informações"
                  brandColor={brandColor}
                />
                <ProfileInfoCard
                  info={profile.info}
                  brandColor={brandColor}
                  phone={profile.whatsappNumber}
                  onPhone={openWhatsapp}
                  onMap={openMap}
                  onInstagram={openInstagram}
                  onWebsite={openWebsite}
                />
              </View>
            )}

            {/* Perguntas: o mesmo array vira o FAQPage do HTML. */}
            {faq.length > 0 && (
              <View className="gap-4">
                <SectionHeader
                  icon="help-circle-outline"
                  title={`Perguntas sobre ${displayName}`}
                  brandColor={brandColor}
                />
                <Card>
                  <View className="px-5 py-1.5">
                    {faq.map((item, index) => (
                      <View
                        key={item.question}
                        className={`gap-1 py-3.5 ${index === 0 ? '' : 'border-t border-neutral-100'}`}
                      >
                        <ThemedText
                          role="heading"
                          aria-level={3}
                          className="font-semibold text-sm text-neutral-800"
                        >
                          {item.question}
                        </ThemedText>
                        <ThemedText className="text-sm leading-5 text-neutral-600">
                          {item.answer}
                        </ThemedText>
                      </View>
                    ))}
                  </View>
                </Card>
              </View>
            )}
          </View>
        </View>

        <View className="mt-12 flex-1" />
        <PublicBrandFooter reportSlug={slug} />
      </KeyboardAwareScrollView>
    </View>
  );
}
